import { EllipsisVertical } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@sandtech-hub-monorepo/ui-kit';
import { CertificateStatus } from '@sandtech-hub-monorepo/ehub-client';
import {
  formatDate,
  previewFromUrl,
  splitSkills,
} from '@sandtech-hub-monorepo/ui-kit-site';

import { AchievementEnrollment } from '../../data/useAchievements';
import { CourseBadgeCard } from './CourseBadgeCard';
import { ProgramBadgeCard } from './ProgramBadgeCard';

interface Props {
  enrollment: AchievementEnrollment;
}

export const AchievementCard = ({ enrollment }: Props) => {
  const { program } = enrollment;
  const certificate = program.issuedCertificate;
  const revoked = certificate?.status === CertificateStatus.Revoked;
  // Null for the learner once revoked: the backend hides revoked files.
  const certificateFileUrl = certificate?.pdfUrl ?? certificate?.imageUrl;

  const certificateDate = revoked
    ? certificate?.revokedAt
    : certificate?.issuedAt;
  const skills = splitSkills(program.certificateDefinition?.skillsGained);

  // plannedCourses come in program order, so the index is the course number.
  const badges = enrollment.plannedCourses.flatMap(({ details }, index) =>
    details?.issuedBadge
      ? [
          {
            badge: details.issuedBadge,
            courseName: details.name,
            position: index + 1,
          },
        ]
      : []
  );

  return (
    <div className="relative flex flex-col gap-8 rounded-2xl border border-border-secondary bg-card p-6 lg:flex-row">
      <div className="flex w-full shrink-0 flex-col gap-5 lg:w-1/4 lg:min-w-64">
        {certificate?.imageUrl ? (
          <img
            src={certificate.imageUrl}
            alt={`Certificate for ${program.name}`}
            className="w-full rounded-lg"
            width={1280}
            height={720}
          />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-muted p-6 text-center text-sm text-muted-foreground">
            {revoked
              ? 'This certificate has been revoked.'
              : 'Complete the program to earn your certificate.'}
          </div>
        )}
        {certificateFileUrl && (
          <Button
            variant="outline"
            className="h-12"
            onClick={() => previewFromUrl(certificateFileUrl)}
          >
            View Certificate
          </Button>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4 pr-10">
          <h3 className="text-2xl font-medium text-heading">{program.name}</h3>
          <span className="rounded-md bg-primary/10 px-2 py-1 text-sm text-primary">
            Skills Academy
          </span>
        </div>

        {skills.length > 0 && (
          <div className="text-lg text-muted-foreground">
            <p>Skills Gained:</p>
            <ul className="list-disc pl-6">
              {skills.map((skill) => (
                <li key={skill}>{skill}</li>
              ))}
            </ul>
          </div>
        )}

        {certificateDate && (
          <p className="text-heading">
            <span className="text-primary">
              {revoked ? 'Revoked on:' : 'Certified on:'}
            </span>{' '}
            {formatDate(certificateDate)}
          </p>
        )}

        {certificate && (
          <div className="flex max-w-lg gap-2">
            <span className="min-w-0 flex-1 truncate rounded-lg border border-border-secondary px-3 py-2 text-sm text-heading">
              {certificate.verificationUrl}
            </span>
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={() =>
                navigator.clipboard
                  .writeText(certificate.verificationUrl)
                  .then(() => toast.success('Public link copied'))
              }
            >
              Copy Public Link
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-6 xl:flex-row">
          {badges.length > 0 && (
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              <span className="font-medium text-primary">Course Badges</span>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2 rounded-xl border border-dashed border-primary bg-primary/5 p-4">
                {badges.map(({ badge, courseName, position }) => (
                  <CourseBadgeCard
                    key={badge.id}
                    badge={badge}
                    courseName={courseName}
                    position={position}
                  />
                ))}
              </div>
            </div>
          )}

          {certificate && (
            <div className="flex flex-col gap-4 xl:w-48">
              <span className="font-medium text-success">Program Badge</span>
              <div className="rounded-xl border border-dashed border-success bg-success/5 p-4">
                <ProgramBadgeCard
                  programName={program.name}
                  imageUrl={
                    revoked
                      ? program.certificateDefinition?.revokedBadgeImageUrl ??
                        program.certificateDefinition?.badgeImageUrl
                      : program.certificateDefinition?.badgeImageUrl
                  }
                  revoked={revoked}
                  date={certificateDate}
                  shareUrl={certificate.linkedInShareUrl}
                  revocationReason={certificate.revocationReason}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {certificate &&
        !revoked &&
        (certificate.pdfUrl || certificate.linkedInCertificateUrl) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Certificate actions"
                className="absolute right-6 top-6 h-8 w-8"
              >
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {certificate.pdfUrl && (
                <DropdownMenuItem asChild>
                  <Link
                    to={certificate.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download PDF
                  </Link>
                </DropdownMenuItem>
              )}
              {certificate.linkedInCertificateUrl && (
                <DropdownMenuItem asChild>
                  <Link
                    to={certificate.linkedInCertificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Add to LinkedIn
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
    </div>
  );
};
